import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { filter, first, firstValueFrom } from 'rxjs';

import { environment } from '../../environments/environment';

import { EnvironmentConfigService } from './core/environment-config/environment-config.service';
import { EnvironmentConfiguration } from './core/environment-config/environment-configuration.interface';
import { LoginService } from './services/user-service/login.service';

@Injectable()
export class AppInitializers {
  private readonly injector = inject(Injector);

  /**
   * Router is only needed in special case in this service.
   * Therefore, it is loaded by injector to avoid circular dependency using provideAppInitializer.
   */
  private get router(): Router {
    return this.injector.get(Router);
  }

  public async logInOnAppInit(): Promise<void> {
    const loginService = inject(LoginService);
    const environmentConfigService = inject(EnvironmentConfigService);
    // Cloud only: APP_INITIALIZERs run concurrently, so wait for the runtime config to be loaded
    // before the login call fires — otherwise getBaseUrl cannot resolve the backend host yet and
    // the request hits the FE origin (returning index.html), which hangs bootstrap. On-prem has
    // no runtime config subject to wait on (it would never emit and hang bootstrap). See TARE-18657.
    if (environment.deploymentTarget === 'cloud') {
      await firstValueFrom(
        environmentConfigService.getConfig().pipe(
          filter((config): config is EnvironmentConfiguration => config !== null),
          first(),
        ),
      );
    }
    return new Promise((resolve): void => {
      loginService.getToken().then(
        () => {
          // location without leading '#'
          // eslint-disable-next-line no-restricted-globals
          const enteredUrl: string = window.location.hash.slice(1);
          const urlToNavigate: any[] =
            enteredUrl.startsWith('/afterSales/tabs/') || enteredUrl.startsWith('/recourseBooking/')
              ? [`${enteredUrl}`]
              : ['/recourseBooking/overview'];
          // Default location after login
          // eslint-disable-next-line no-restricted-globals
          setTimeout(async () => this.router.navigate(urlToNavigate).then(() => resolve()), 0);
        },
        () => {
          // no user found - redirect to logout page
          this.router.navigate(['/logout']).then(() => {
            // timeout needed in order that actions are not triggered simultaneously
            // eslint-disable-next-line no-restricted-globals
            setTimeout(() => {
              // eslint-disable-next-line no-console
              console.log('No User....');
            });
          });
          resolve(); // resolve anyway in order to show logout page
        },
      );
    });
  }
}
